import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../hooks/auth/use-auth';
import * as storage from '../../../integrations/lavva/storage';
import { CredentialData, KeycloakAccountCredentials } from '../types/types';

export const useCredentials = () => {
  const { i18n } = useTranslation();
  const { keycloak } = useAuth();
  const [totp, setTotp] = useState<CredentialData>({ credentials: null, loading: true });
  const [passwordless, setPasswordless] = useState<CredentialData>({ credentials: null, loading: true });
  const theme = storage.getItem('theme');

  const getCredentialIfExists = (
    type: 'otp' | 'webauthn-passwordless',
    credentials: KeycloakAccountCredentials[],
    setState: Dispatch<SetStateAction<CredentialData>>,
  ) => {
    const typeCredentials = credentials.find((cred) => cred.type === type)?.userCredentialMetadatas;

    if (typeCredentials) setState({ credentials: typeCredentials, loading: false });
  };

  useEffect(() => {
    const checkCredentialsStatus = async () => {
      try {
        if (keycloak.authenticated) {
          try {
            const accountUrl = `${keycloak.authServerUrl}/realms/${keycloak.realm}/account`;
            const credentialsResponse = await fetch(`${accountUrl}/credentials`, {
              headers: {
                Authorization: `Bearer ${keycloak.token}`,
                Accept: 'application/json',
              },
            });

            if (credentialsResponse.ok) {
              const credentials: KeycloakAccountCredentials[] = await credentialsResponse.json();

              getCredentialIfExists('otp', credentials, setTotp);
              getCredentialIfExists('webauthn-passwordless', credentials, setPasswordless);
            }
          } catch (apiError) {
            console.log('Błąd podczas pobierania credentials z API:', apiError);
          }
        }
      } catch (error) {
        console.error('Error loading keycloak user profile:', error);
      } finally {
        setTotp((prev) => ({ ...prev, loading: false }));
        setPasswordless((prev) => ({ ...prev, loading: false }));
      }
    };

    checkCredentialsStatus();
  }, [keycloak]);

  const configureTOTP = () => {
    keycloak.login({ action: 'CONFIGURE_TOTP', locale: i18n.language, theme });
  };

  const configurePasswordless = () => {
    keycloak.login({ action: 'webauthn-register-passwordless', locale: i18n.language, theme });
  };

  const handleRemoveCredential = (id: string) => {
    keycloak.login({ action: `delete_credential:${id}`, locale: i18n.language, theme });
  };

  return {
    totp,
    passwordless,
    configureTOTP,
    configurePasswordless,
    handleRemoveCredential,
  };
};
